<template>
	<Sidebar v-if="hasPermission('np.backoffice.promocodes.add')" v-model:visible="createNewSidebarVisible"
		class="p-sidebar-lg modern-sidebar" position="right" :showCloseIcon="false">
		<Loading v-if="creating" :title="'Creating ' + entityName + '...'" :full="false" />
		<div v-else class="promo-form-container">
			<div class="flex align-items-center mb-4">
				<h3 class="text-xl font-semibold m-0">✨ Create {{ entityName }}</h3>
			</div>
			<Form :fields="fields" :context="newEntity" @onAction="createEntity" @onChange="handleFormChange"
				:actionLabel="'Create'" :actionIcon="'pi pi-plus-circle'" class="promo-form" />
		</div>
	</Sidebar>

	<Sidebar v-if="hasPermission('np.backoffice.promocodes.editdelete')" v-model:visible="updateSidebarVisible"
		class="p-sidebar-lg modern-sidebar" position="right" :showCloseIcon="false">
		<Loading v-if="updating" :title="'Updating ' + entityName + '...'" :full="false" />
		<div v-else class="promo-form-container">
			<div class="flex align-items-center mb-4">
				<h3 class="text-xl font-semibold m-0">✏️ Edit {{ entityName }}</h3>
			</div>
			<Form :fields="fields" :context="selectedEntity" @onChange="handleFormChange" @onAction="updateEntity"
				:actionLabel="'Update'" :actionIcon="'pi pi-cloud-upload'" class="promo-form" />
		</div>
	</Sidebar>

	<Dialog v-model:visible="generateSubCodesVisible" modal header="Generate Sub-codes" :style="{ width: '35rem' }"
		class="modern-dialog">
		<template #header>
			<div class="inline-flex align-items-center justify-content-center gap-3">
				<i class="fa fa-code text-blue-500 text-xl"></i>
				<h3 class="font-semibold white-space-nowrap m-0 p-0 text-gray-800">Generate Sub-codes</h3>
			</div>
		</template>
		<div class="p-4">
			<div class="flex flex-column gap-4">
				<div class="bg-blue-50 border-round-lg p-4 mb-2">
					<p class="text-gray-700 mb-2">
						Generate unique sub-codes for <span class="font-semibold text-blue-600">{{
							selectedPromoCodeForGeneration?.campaignName }}</span>
					</p>
				</div>
				<div>
					<label for="count" class="font-semibold text-gray-700 mb-2 block">Count</label>
					<InputNumber id="count" v-model="generateSubCodesForm.count" :min="1" :max="10000" class="w-full" />
				</div>
				<div>
					<label for="length" class="font-semibold text-gray-700 mb-2 block">Length (excluding prefix)</label>
					<InputNumber id="length" v-model="generateSubCodesForm.length" :min="4" :max="20" class="w-full" />
				</div>
				<div>
					<label for="prefix" class="font-semibold text-gray-700 mb-2 block">Prefix (optional)</label>
					<InputText id="prefix" v-model="generateSubCodesForm.prefix" class="w-full"
						placeholder="e.g. PROMO-" />
				</div>

				<div v-if="generateSubCodesForm.length && generateSubCodesForm.length > 0" class="mt-2">
					<label class="font-semibold text-sm text-gray-700 mb-2 block">Preview examples:</label>
					<div class="bg-gray-50 border-round-lg p-3">
						<div v-for="example in subCodeExamples" :key="example"
							class="font-mono text-sm mb-1 text-gray-800">
							{{ example }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<template #footer>
			<div class="flex gap-3 justify-content-end">
				<Button label="Cancel" icon="pi pi-times" @click="generateSubCodesVisible = false" severity="secondary"
					outlined class="px-4 py-2 border-round-lg" />
				<Button label="Generate" icon="pi pi-check" @click="generateSubCodes" :loading="generatingSubCodes"
					class="px-4 py-2 border-round-lg font-semibold" />
			</div>
		</template>
	</Dialog>

	<Dialog v-model:visible="subCodesManagementVisible" modal header="Sub-codes Management"
		:style="{ width: '90vw', height: '80vh' }" class="modern-dialog bg-white" :maximizable="true"
		:dismissableMask="true">
		<template #header>
			<div class="inline-flex align-items-center justify-content-center gap-3">
				<i class="fa fa-cogs text-purple-500 text-xl"></i>
				<h3 class="font-semibold white-space-nowrap m-0 p-0 text-gray-800">Sub-codes Management</h3>
			</div>
		</template>
		<!-- 
			IMPORTANT BUSINESS RULE: Sub-codes should NEVER be deleted once generated.
			They serve as audit trail and history. Even unredeemed sub-codes may have been 
			distributed to users and deleting them would break that distribution.
		-->
		<div class="flex flex-column h-full p-4">
			<div class="bg-purple-50 border-round-lg p-4 mb-4">
				<div class="flex justify-content-between align-items-center">
					<div>
						<h4 class="m-0 text-gray-800">{{ selectedPromoCodeForManagement?.campaignName }}</h4>
						<p class="text-sm text-gray-600 m-0 mt-1">
							<span v-if="subCodeSearchFilter && subCodeSearchFilter.trim()">
								{{ filteredSubCodeDetails.length }} of {{ subCodeDetails.length }} sub-codes match your
								search,
								{{filteredSubCodeDetails.filter(s => s.isRedeemed).length}} redeemed,
								{{filteredSubCodeDetails.filter(s => !s.isRedeemed).length}} available
							</span>
							<span v-else>
								{{ subCodeDetails.length }} total sub-codes,
								{{subCodeDetails.filter(s => s.isRedeemed).length}} redeemed,
								{{filteredSubCodeDetails.filter(s => !s.isRedeemed).length}} available
							</span>
						</p>
					</div>
					<div class="flex gap-2">
						<Button label="Export" icon="fa fa-download" @click="exportSubCodesToCSV"
							:disabled="!subCodeDetails || subCodeDetails.length === 0" class="export-button" />
						<Button label="Generate More" icon="fa fa-plus"
							@click="openGenerateSubCodesDialog(selectedPromoCodeForManagement)"
							:disabled="!selectedPromoCodeForManagement?.hasSubCodes" class="generate-more-button" />
					</div>
				</div>
			</div>

			<div class="flex-1 flex flex-column overflow-hidden bg-white border-round-lg shadow-1">
				<div class="table-header-section flex-shrink-0">
					<div class="flex align-items-center justify-content-between">
						<div class="flex align-items-center gap-3">
							<div class="bg-purple-100 p-2 border-round-lg">
								<i class="fa fa-list text-purple-600"></i>
							</div>
							<h2 class="text-lg font-semibold text-gray-800 m-0">Sub-codes Overview</h2>
						</div>
						<div class="p-input-icon-left">
							<i class="pi pi-search" />
							<InputText v-model="subCodeSearchFilter" placeholder="Search sub-codes..."
								class="subcodes-search-input" />
						</div>
					</div>
				</div>

				<div class="flex-1 overflow-hidden">
					<DataTable :value="filteredSubCodeDetails" :loading="loadingSubCodeDetails" dataKey="code"
						:paginator="true" :rows="20" :rowsPerPageOptions="[10, 20, 50, 100]"
						class="modern-datatable subcodes-datatable" scrollable scrollHeight="flex" stripedRows>
						<template #empty>
							<div class="text-center py-8">
								<i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
								<p class="text-gray-500 text-lg m-0">No sub-codes found</p>
								<p class="text-gray-400 text-sm m-0 mt-2">Generate sub-codes to get started</p>
							</div>
						</template>
						<template #loading>
							<div class="text-center py-6">
								<i class="fa fa-spinner fa-spin text-purple-500 text-2xl mb-2"></i>
								<p class="text-gray-600 m-0">Loading sub-codes...</p>
							</div>
						</template>

						<Column field="code" header="" sortable>
							<template #header>
								<div class="flex align-items-center gap-2">
									<i class="fa fa-code text-gray-600"></i>
									<span class="font-semibold text-gray-700">Sub-code</span>
								</div>
							</template>
							<template #body="{ data }">
								<div class="flex align-items-center gap-3 py-1">
									<div class="bg-gray-100 p-2 border-round-lg">
										<i class="fa fa-ticket text-gray-600"></i>
									</div>
									<div class="flex align-items-center gap-2">
										<span class="font-mono font-semibold text-gray-800">{{ data.code }}</span>
										<Button icon="pi pi-copy" size="small" text @click="copyToClipboard(data.code)"
											v-tooltip="'Copy to clipboard'" class="copy-button" />
									</div>
								</div>
							</template>
						</Column>

						<Column field="isRedeemed" header="" sortable>
							<template #header>
								<div class="flex align-items-center gap-2">
									<i class="fa fa-circle text-gray-600"></i>
									<span class="font-semibold text-gray-700">Status</span>
								</div>
							</template>
							<template #body="{ data }">
								<div class="flex">
									<div :class="data.isRedeemed ? 'status-redeemed' : 'status-available'"
										class="status-badge">
										<i :class="data.isRedeemed ? 'fa fa-check-circle' : 'fa fa-clock'"
											class="text-sm"></i>
										<span>{{ data.isRedeemed ? 'Redeemed' : 'Available' }}</span>
									</div>
								</div>
							</template>
						</Column>

						<Column field="redemptionCount" header="" sortable>
							<template #header>
								<div class="flex align-items-center gap-2">
									<i class="fa fa-hashtag text-gray-600"></i>
									<span class="font-semibold text-gray-700">Number of redemptions</span>
								</div>
							</template>
							<template #body="{ data }">
								<span class="text-gray-700">
									{{ data.redemptionCount || 0 }}
									<span class="text-gray-400">/ {{ data.subCodeRedeemLimit > 0 ? data.subCodeRedeemLimit : '∞' }}</span>
								</span>
							</template>
						</Column>

						<Column field="lastRedemptionAt" header="" sortable>
							<template #header>
								<div class="flex align-items-center gap-2">
									<i class="fa fa-calendar text-gray-600"></i>
									<span class="font-semibold text-gray-700">Last redemption timestamp</span>
								</div>
							</template>
							<template #body="{ data }">
								<span v-if="data.lastRedemptionAt" class="text-gray-700">{{
									formatDateTimeFullTimezone(data.lastRedemptionAt)
								}}</span>
								<span v-else class="text-gray-400">-</span>
							</template>
						</Column>

					</DataTable>
				</div>
			</div>
		</div>
	</Dialog>

	<div class="page-container">
		<div class="max-w-7xl mx-auto p-6" v-if="hasPermission('np.backoffice.promocodes.show')">
			<!-- Header Section -->
			<div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
				<div class="flex align-items-center justify-content-between">
					<div class="flex align-items-center gap-4">
						<div class="header-icon-container">
							<i class="fa fa-tags text-white text-2xl"></i>
						</div>
						<div>
							<h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
							<p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
						</div>
					</div>
					<Button v-if="hasPermission('np.backoffice.promocodes.add')" @click="createNewSidebarVisible = true"
						:label="'New ' + entityName" icon="fa fa-plus" class="create-button" />
				</div>
			</div>

			<!-- Filters Section -->
			<div class="bg-white border-round-2xl shadow-1 p-0 mb-6">
				<div class="table-header-section border-round-t-2xl">
					<div class="flex align-items-center gap-3 mb-0">
						<div class="bg-blue-100 p-2 border-round-lg">
							<i class="fa fa-filter text-blue-600"></i>
						</div>
						<h2 class="text-xl font-semibold text-gray-800 m-0">Filters & Search</h2>
					</div>
				</div>

			<div class="filters-grid p-4">
				<div class="filter-column">
					<label class="font-semibold text-gray-700 mb-2 block">Type filter</label>
					<Dropdown v-model="filters.type" display="chip" class="w-full" :options="typeFilterItems"
						:filter="false" optionLabel="title" @change="applyFilter" optionValue="id"
						placeholder="Select Promo code types" />
				</div>
				<div class="filter-column">
					<label class="font-semibold text-gray-700 mb-2 block">Show expired</label>
					<div class="flex align-items-center gap-2 mt-3 w-full">
						<InputSwitch v-model="filters.showExpired" @change="applyFilter" class="flex-shrink-0" />
						<span class="text-gray-600 flex-1 white-space-nowrap">Include expired codes</span>
					</div>
				</div>
				<div class="filter-column">
					<label class="font-semibold text-gray-700 mb-2 block">Search by code</label>
					<div class="p-inputgroup mb-2 w-full">
						<InputText v-model="filters.code" placeholder="Enter promo code..." @input="onCodeSearch"
							class="search-input" />
						<Button icon="pi pi-search" @click="applyFilter" class="search-button" />
					</div>
					<div class="flex align-items-center gap-2 w-full">
						<InputSwitch v-model="filters.includeSubCodes" @change="applyFilter"
							class="flex-shrink-0" />
						<span class="text-sm text-gray-600 flex-1 white-space-nowrap">Include sub-codes in
							search</span>
					</div>
				</div>
				<div class="filter-column">
					<label class="font-semibold text-gray-700 mb-2 block">Search by campaign</label>
					<div class="p-inputgroup mb-2 w-full">
						<InputText v-model="filters.campaign" placeholder="Enter campaign name..." @input="onCampaignSearch"
							class="search-input" />
						<Button icon="pi pi-search" @click="applyFilter" class="search-button" />
					</div>
				</div>
			</div>
			</div>

			<Loading v-if="loading" class="mt-6" :full="false" :title="'Loading ' + pageTitle + '...'" />

			<!-- Main Content -->
			<div v-else class="bg-white border-round-2xl shadow-1 overflow-hidden">
				<!-- Table Header -->
				<div class="table-header-section">
					<div class="flex align-items-center justify-content-between">
						<div class="flex align-items-center gap-3">
							<div class="bg-green-100 p-2 border-round-lg">
								<i class="fa fa-list text-green-600"></i>
							</div>
							<h2 class="text-xl font-semibold text-gray-800 m-0">Promo Codes Overview ({{ entities.length }})
							</h2>
						</div>
						<div v-if="selectedPromoCodes.length > 0 && hasPermission('np.backoffice.promocodes.editdelete')" 
							class="flex align-items-center gap-3">
							<span class="text-gray-600">{{ selectedPromoCodes.length }} selected</span>
							<Button 
								@click="confirmBulkDelete" 
								label="Delete Selected" 
								icon="fa fa-trash" 
								severity="danger"
								class="bulk-delete-button" 
							/>
						</div>
					</div>
				</div>

				<DataTable :value="entities" dataKey="id" @row-click="onRowClicked" class="modern-datatable"
					:rowHover="true" responsiveLayout="scroll" :lazy="true" :paginator="true"
					:rows="pagination.pageSize" :totalRecords="pagination.totalRecords" :first="pagination.first"
					:rowsPerPageOptions="[10, 20, 50, 100]" @page="onPageChange"
					currentPageReportTemplate="Showing {first} to {last} of {totalRecords} promo codes"
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
					stripedRows>
					<template #empty>
						<div class="text-center py-8">
							<i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
							<p class="text-gray-500 text-lg m-0">No {{ pageTitle }} found</p>
							<p class="text-gray-400 text-sm m-0 mt-2">Create your first promo code to get started</p>
						</div>
					</template>
					<template #loading>
						<div class="text-center py-6">
							<i class="fa fa-spinner fa-spin text-blue-500 text-2xl mb-2"></i>
							<p class="text-gray-600 m-0">Loading {{ pageTitle }}...</p>
						</div>
					</template>

				<Column v-if="hasPermission('np.backoffice.promocodes.editdelete')" :style="{ width: '3rem' }">
					<template #header>
						<div class="checkbox-header-wrapper">
							<Checkbox v-model="selectAll" @change="toggleSelectAll" :binary="true" />
						</div>
					</template>
					<template #body="{ data }">
						<div @click.stop="toggleSelection(data.id)" class="checkbox-cell-wrapper">
							<Checkbox 
								:modelValue="isSelected(data.id)"
								:binary="true" 
							/>
						</div>
					</template>
				</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-bullhorn text-gray-600"></i>
								<span class="font-semibold text-gray-700">Campaign</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="flex flex-column gap-2">
								<div class="flex align-items-center gap-2">
									<div class="bg-blue-100 p-2 border-round-lg">
										<i class="fa fa-bullhorn text-blue-600 text-sm"></i>
									</div>
									<span class="text-gray-700 text-sm font-medium">{{ data.campaignName || '-'
									}}</span>
								</div>
								<div class="flex">
									<div class="status-badge type-badge">
										<i class="fa fa-tag text-sm"></i>
										<span>{{ data.promoCodeType }}</span>
									</div>
								</div>
							</div>
						</template>
					</Column>

					<Column header="" field="code">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-code text-gray-600"></i>
								<span class="font-semibold text-gray-700">Code & Sub-codes</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="flex flex-column gap-2">
								<div class="flex align-items-center gap-3">
									<div class="bg-gray-100 p-2 border-round-lg">
										<i class="fa fa-ticket text-gray-600"></i>
									</div>
									<div>
										<span v-if="!data.hasSubCodes" class="font-mono font-semibold text-gray-800">{{
											data.code }}</span>
										<span v-else class="text-gray-400 italic">Uses sub-codes</span>
									</div>
								</div>
								<div class="flex align-items-center gap-2" v-if="data.hasSubCodes">
									<div class="status-badge subcodes-badge">
										<i class="fa fa-code text-sm"></i>
										<span>{{ data.subCodes?.length || 0 }} sub-codes</span>
									</div>
									<Button v-if="hasPermission('np.backoffice.promocodes.editdelete')" label="Manage"
										icon="fa fa-cog" size="small" text @click.stop="openSubCodesManagement(data)"
										v-tooltip="'Manage sub-codes'" class="manage-button" />
								</div>
							</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-calendar text-gray-600"></i>
								<span class="font-semibold text-gray-700">Valid from</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="text-sm text-gray-700">{{ formatDate(data.validFromUtc) }}</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-calendar-times text-gray-600"></i>
								<span class="font-semibold text-gray-700">Valid to</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="text-sm text-gray-700">{{ formatDate(data.validToUtc) }}</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-clock text-gray-600"></i>
								<span class="font-semibold text-gray-700">Perk active days</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="text-sm text-gray-700">{{ data.perkActiveDays == null ? 7 : data.perkActiveDays
								}}</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-calendar-plus text-gray-600"></i>
								<span class="font-semibold text-gray-700">Trial days</span>
							</div>
						</template>
						<template #body="{ data }">
							<span class="text-gray-700">{{ data.freeDays }}</span>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-user text-gray-600"></i>
								<span class="font-semibold text-gray-700">Created by</span>
							</div>
						</template>
						<template #body="{ data }">
							<span class="text-gray-700">{{ data?.createdByDisplayName }}</span>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-cog text-gray-600"></i>
								<span class="font-semibold text-gray-700">Actions</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="flex gap-2">
								<Button v-if="hasPermission('np.backoffice.promocodes.editdelete')"
									@click.stop="onRowClicked({ data })" type="button" icon="fa fa-edit"
									class="action-button edit-button" size="small" v-tooltip.top="'Edit Promo Code'" />
								
							</div>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
	</div>
</template>

<script>
/* eslint-disable vue/no-unused-components */

import { ref, onMounted, inject, computed, watch } from 'vue';

import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import apiClient from '@/services/axios';

export default {
	inject: ['hasPermission'],
	components: {},
	setup() {
		const storeActionGetEntities = 'promoCodes/getPromoCodeListItems';
		const storeGetterGetEntities = 'promoCodes/promoCodeListItems';
		const storeActionCreateEntity = 'promoCodes/create';
		const storeActionUpdateEntity = 'promoCodes/update';
		const storeActionDeleteEntity = 'promoCodes/delete';
		const entityName = 'Promo Code';
		const pageTitle = 'Promo Codes';
		const pageDescription = 'Here you can manage your Promo Codes';

		const fields = ref([]);

		const loading = ref(true);

		const creating = ref(false);
		const updating = ref(false);

		const memberCards = ref([]);
		const perks = ref([]);
		const staffUsers = ref([])

		const useFormatDate = inject('useFormatDate');

		const store = useStore();
		const toast = useToast();
		const confirm = useConfirm();

		const entities = ref([]);

		const pagination = ref({
			first: 0,
			page: 0,
			pageSize: 10,
			totalRecords: 0
		});

		const searchTimeout = ref(null);

		// Bulk delete state
		const selectedPromoCodes = ref([]);
		const selectAll = ref(false);

		// Timezone detection
		const detectedTimeZone = computed(() => {
			return Intl.DateTimeFormat().resolvedOptions().timeZone;
		});

		function initializeRedemptionTimeControl(config) {
			if (!config.perkRedemptionTimeControl) {
				config.perkRedemptionTimeControl = {
					isRedemptionTimeLimited: false,
					lockedFromTime: null,
					unlockedAtTime: null,
					lockedFromTimeDate: null,
					unlockedAtTimeDate: null,
					timeZoneId: detectedTimeZone.value
				};
			} else {
				// Initialize Date objects for time pickers from string times
				if (config.perkRedemptionTimeControl.lockedFromTime) {
					config.perkRedemptionTimeControl.lockedFromTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.lockedFromTime);
				}
				if (config.perkRedemptionTimeControl.unlockedAtTime) {
					config.perkRedemptionTimeControl.unlockedAtTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.unlockedAtTime);
				}
				// Ensure timezone is set
				if (!config.perkRedemptionTimeControl.timeZoneId) {
					config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
				}
			}
		}

		function parseTimeToDate(timeString) {
			if (!timeString) return null;
			const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
			
			// Create a date object in local time
			const date = new Date();
			date.setHours(hours, minutes, 0, 0);
			
			return date;
		}

		function formatTimeFromDate(date) {
			if (!date) return null;
			
			// Get time in local timezone
			const hours = date.getHours().toString().padStart(2, '0');
			const minutes = date.getMinutes().toString().padStart(2, '0');
			const seconds = "00"; // Always use 00 for seconds
			
			return `${hours}:${minutes}:${seconds}`; // TimeSpan format for JSON.NET
		}

		const newEntity = ref({
			promoCodeType: 'General',
			mustBeTrialPeriodEligible: false,
			campaignName: null,
			redeemLimit: 1,
			code: null,
			userId: { id: null },
			validFromUtc: null,
			validToUtc: null,
			perkActiveDays: 7,
			freeDays: 0,
			freeDaysExpireAtDate: null,
			freeDaysCanBypassAntiFraud: false,
			perkConfigs: [{ 
				id: null, 
				venueId: null, 
				isFromNightPay: false,
				perkRedemptionTimeControl: {
					isRedemptionTimeLimited: false,
					lockedFromTime: null,
					unlockedAtTime: null,
					lockedFromTimeDate: null,
					unlockedAtTimeDate: null,
					timeZoneId: detectedTimeZone.value
				}
			}],
			partnershipId: { id: null },
			memberCardConfigs: [{ id: null }],
			hasSubCodes: false,
			subCodes: [],
			allowRedemptionOfMultipleSubCodes: false,
			subCodeRedeemLimit: 1,
			hasOverrideExpiration: false,
			overrideExpirationValue: 0,
			overrideExpirationUnit: "Months",
			hasCustomSubscriptionPricing: false,
			subscriptionPriceConfigs: []
		});

		const selectedEntity = ref({});

		const createNewSidebarVisible = ref(false);
		const updateSidebarVisible = ref(false);
		const generateSubCodesVisible = ref(false);
		const generatingSubCodes = ref(false);

		const generateSubCodesForm = ref({
			count: 10,
			length: 8,
			prefix: ''
		});

		const selectedPromoCodeForGeneration = ref(null);
		const selectedPromoCodeForManagement = ref(null);
		const subCodesManagementVisible = ref(false);
		const subCodeDetails = ref([]);
		const loadingSubCodeDetails = ref(false);
		const subCodeSearchFilter = ref('');

		const filters = ref({
			showExpired: false,
			type: "All",
			code: "",
			campaign: "",
			page: 0,
			pageSize: 10,
			includeSubCodes: false
		})


		const typeFilterItems = [
			{ id: 'All', title: 'All' },
			{ id: 'General', title: 'General' },
			{ id: 'Signup', title: 'Signup' },
			{ id: 'OnlySubscribers', title: 'OnlySubscribers' }
		];

		// Computed property for generating example sub-codes
		const subCodeExamples = computed(() => {
			if (!generateSubCodesForm.value.length || generateSubCodesForm.value.length < 1) {
				return [];
			}

			const examples = [];
			const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
			const prefix = generateSubCodesForm.value.prefix || '';
			const length = generateSubCodesForm.value.length;

			for (let i = 0; i < 5; i++) {
				let code = prefix;
				for (let j = 0; j < length; j++) {
					code += chars.charAt(Math.floor(Math.random() * chars.length));
				}
				examples.push(code);
			}

			return examples;
		});

		// Computed property for filtering sub-codes
		const filteredSubCodeDetails = computed(() => {
			if (!subCodeSearchFilter.value || subCodeSearchFilter.value.trim() === '') {
				return subCodeDetails.value;
			}

			const searchTerm = subCodeSearchFilter.value.toLowerCase().trim();

			return subCodeDetails.value.filter(subCode => {
				return (
					subCode.code.toLowerCase().includes(searchTerm) ||
					(subCode.redeemedByUserName && subCode.redeemedByUserName.toLowerCase().includes(searchTerm)) ||
					(subCode.redeemedByUserEmail && subCode.redeemedByUserEmail.toLowerCase().includes(searchTerm))
				);
			});
		});

		// Bulk delete functions
		function toggleSelectAll() {
			if (selectAll.value) {
				selectedPromoCodes.value = [...entities.value.map(e => e.id)];
			} else {
				selectedPromoCodes.value = [];
			}
		}

		function toggleSelection(promoCodeId) {
			const index = selectedPromoCodes.value.indexOf(promoCodeId);
			if (index > -1) {
				selectedPromoCodes.value.splice(index, 1);
			} else {
				selectedPromoCodes.value.push(promoCodeId);
			}
			
			// Update select all checkbox state
			selectAll.value = entities.value.length > 0 && selectedPromoCodes.value.length === entities.value.length;
		}

		function isSelected(promoCodeId) {
			return selectedPromoCodes.value.includes(promoCodeId);
		}

		function confirmBulkDelete() {
			if (selectedPromoCodes.value.length === 0) {
				toast.add({ 
					severity: 'warn', 
					summary: 'No Selection', 
					detail: 'Please select at least one promo code to delete', 
					life: 3000 
				});
				return;
			}

			confirm.require({
				message: `Are you sure you want to delete ${selectedPromoCodes.value.length} promo code(s)?`,
				header: 'Confirm Bulk Delete',
				icon: 'pi pi-exclamation-triangle',
				accept: async () => {
					await bulkDeletePromoCodes();
				},
				reject: () => { },
			});
		}

		async function bulkDeletePromoCodes() {
			loading.value = true;

			try {
				const result = await store.dispatch('promoCodes/bulkDelete', selectedPromoCodes.value);
				
				if (result.failedCount > 0) {
					toast.add({ 
						severity: 'warn', 
						summary: 'Partially Completed', 
						detail: `Deleted ${result.successCount} promo code(s). Failed to delete ${result.failedCount}.`, 
						life: 5000 
					});
				} else {
					toast.add({ 
						severity: 'success', 
						summary: 'Success', 
						detail: `Successfully deleted ${result.successCount} promo code(s)`, 
						life: 3000 
					});
				}

				// Clear selection
				selectedPromoCodes.value = [];
				selectAll.value = false;

				// Reload entities
				await reloadEntities();
			} catch (err) {
				toast.add({ 
					severity: 'error', 
					summary: 'Error', 
					detail: 'Could not delete promo codes: ' + err, 
					life: 3000 
				});
			}

			loading.value = false;
		}

		function onRowClicked(payload) {
			selectedEntity.value = JSON.parse(JSON.stringify(payload.data));

			selectedEntity.value.validFromUtc = new Date(selectedEntity.value.validFromUtc);
			selectedEntity.value.validToUtc = new Date(selectedEntity.value.validToUtc);
			selectedEntity.value.perkActiveDays = selectedEntity.value.perkActiveDays == null ? 7 : selectedEntity.value.perkActiveDays;

			// Initialize perkRedemptionTimeControl for existing perk configs
			if (selectedEntity.value.perkConfigs && selectedEntity.value.perkConfigs.length > 0) {
				selectedEntity.value.perkConfigs.forEach(perkConfig => {
					initializeRedemptionTimeControl(perkConfig);
				});
			}

			// Sub-codes are managed through the dedicated management dialog, not the sidebar form
			// No transformation needed here since there's no sub-codes field in the form

			handleFormChange(selectedEntity.value)
			updateSidebarVisible.value = true;
		}





		function getCreatedBy(userId) {
			var user = staffUsers.value?.find(x => x.id == userId)
			return user ? user?.firstName + " " + user?.lastName : "Unknown"
		}

		function formattedPerks(perkConfigs) {
			if (perkConfigs?.id == null || perkConfigs.length == 0) return '';

			const perkList = [];
			perkConfigs.forEach((perkConfig) => {
				console.log(perkConfig)
				const perk = store.getters['perks/perk'](perkConfig.id);

				if (perk != null) perkList.push(perk.title);
			});

			return perkList.join(', ');
		}

		function confirmDeleteEntity(data) {
			confirm.require({
				message: 'Are you sure you want to delete the ' + entityName + '?',
				header: 'Please confirm',
				icon: 'pi pi-exclamation-triangle',
				accept: async () => {
					loading.value = true;

					try {
						await store.dispatch(storeActionDeleteEntity, data.id);
						await reloadEntities();

						toast.add({ severity: 'success', summary: 'Success', detail: entityName + ' deleted', life: 3000 });
					} catch (err) {
						toast.add({ severity: 'error', summary: 'Error', detail: 'Could not delete ' + entityName + ': ' + err, life: 3000 });
					}

					loading.value = false;
				},
				reject: () => { },
			});
		}

		function handleFormChange(context) {
			// Prevent disabling sub-codes if the promo code already has sub-codes
			if (context.id && !context.hasSubCodes && context.subCodes && context.subCodes.length > 0) {
				// Revert the toggle back to true
				context.hasSubCodes = true;

				toast.add({
					severity: 'warn',
					summary: 'Cannot Disable Sub-codes',
					detail: 'This promo code already contains sub-codes. Sub-codes cannot be disabled once created as they serve as permanent audit trail.',
					life: 5000
				});

				return; // Exit early to prevent further processing
			}

		if (context.promoCodeType == "Signup" && fields.value.findIndex(x => x.id == "freeDays") < 0) {
			fields.value.splice(7, 0, {
				id: 'freeDays',
				type: 'number-input',
				title: 'Free days',
			})
			fields.value.splice(8, 0, {
				id: 'freeDaysExpireAtDate',
				type: 'calendar',
				title: 'Free days expire at date *',
			})
				// fields.value.splice(9, 0, {
				// 	id: 'freeDaysCanBypassAntiFraud',
				// 	type: 'switch',
				// 	title: 'Free days can bypass anti-fraud',
				// })
			}
			else if (context.promoCodeType != "Signup") {
				if (fields.value[7].id == "freeDays") {
					fields.value.splice(7, 1)
				}
				if (fields.value[7].id == "freeDaysExpireAtDate") {
					fields.value.splice(7, 1)
				}
				// if (fields.value[7].id == "freeDaysCanBypassAntiFraud") {
				// 	fields.value.splice(7, 1)
				// }
			}

			// Hide/show code field based on sub-codes toggle
			const codeFieldIndex = fields.value.findIndex(x => x.id == "code");
			const codeField = {
				id: 'code',
				type: 'text-input',
				title: 'Code',
			};

			if (context.hasSubCodes) {
				// Remove code field when sub-codes are enabled
				if (codeFieldIndex >= 0) {
					fields.value.splice(codeFieldIndex, 1);
				}
			} else {
				// Add code field when sub-codes are disabled (if not already present)
				if (codeFieldIndex < 0) {
					// Insert code field after redeemLimit (index 3) and before hasSubCodes
					const hasSubCodesIndex = fields.value.findIndex(x => x.id == "hasSubCodes");
					const insertIndex = hasSubCodesIndex >= 0 ? hasSubCodesIndex : 4;
					fields.value.splice(insertIndex, 0, codeField);
				}
			}

			// Hide/show redeem limit field based on sub-codes toggle
			const redeemLimitFieldIndex = fields.value.findIndex(x => x.id == "redeemLimit");
			const redeemLimitField = {
				id: 'redeemLimit',
				type: 'number-input',
				title: 'Redeem limit',
			};

			if (context.hasSubCodes) {
				// Remove redeem limit field when sub-codes are enabled
				if (redeemLimitFieldIndex >= 0) {
					fields.value.splice(redeemLimitFieldIndex, 1);
				}
			} else {
				// Add redeem limit field when sub-codes are disabled (if not already present)
				if (redeemLimitFieldIndex < 0) {
					// Insert redeem limit field after campaignName (index 1)
					fields.value.splice(2, 0, redeemLimitField);
				}
			}

			// Hide/show allowRedemptionOfMultipleSubCodes field based on sub-codes toggle
			const allowMultipleSubCodesFieldIndex = fields.value.findIndex(x => x.id == "allowRedemptionOfMultipleSubCodes");
			const allowMultipleSubCodesField = {
				id: 'allowRedemptionOfMultipleSubCodes',
				type: 'switch',
				title: 'User can redeem multiple sub-codes',
				config: {
					tooltip: 'When enabled, users can redeem multiple different sub-codes from this promocode. Each sub-code can still only be used once.'
				}
			};

			if (context.hasSubCodes) {
				// Add allowRedemptionOfMultipleSubCodes field when sub-codes are enabled (if not already present)
				if (allowMultipleSubCodesFieldIndex < 0) {
					// Insert after hasSubCodes field
					const hasSubCodesIndex = fields.value.findIndex(x => x.id == "hasSubCodes");
					const insertIndex = hasSubCodesIndex >= 0 ? hasSubCodesIndex + 1 : 4;
					fields.value.splice(insertIndex, 0, allowMultipleSubCodesField);
				}
			} else {
				// Remove allowRedemptionOfMultipleSubCodes field when sub-codes are disabled
				if (allowMultipleSubCodesFieldIndex >= 0) {
					fields.value.splice(allowMultipleSubCodesFieldIndex, 1);
					// Reset the value when sub-codes are disabled
					context.allowRedemptionOfMultipleSubCodes = false;
				}
			}

			// Hide/show subCodeRedeemLimit field based on sub-codes toggle
			const subCodeRedeemLimitFieldIndex = fields.value.findIndex(x => x.id == "subCodeRedeemLimit");
			const subCodeRedeemLimitField = {
				id: 'subCodeRedeemLimit',
				type: 'number-input',
				title: 'Sub-code redeem limit',
				config: {
					tooltip: 'How many times each individual sub-code can be redeemed. Set to 0 for unlimited. Default is 1 (each sub-code can only be used once).'
				}
			};

			if (context.hasSubCodes) {
				// Add subCodeRedeemLimit field when sub-codes are enabled (if not already present)
				if (subCodeRedeemLimitFieldIndex < 0) {
					// Insert after allowRedemptionOfMultipleSubCodes field (or after hasSubCodes if not present)
					const allowMultipleIdx = fields.value.findIndex(x => x.id == "allowRedemptionOfMultipleSubCodes");
					const insertIndex = allowMultipleIdx >= 0 ? allowMultipleIdx + 1 : fields.value.findIndex(x => x.id == "hasSubCodes") + 1;
					fields.value.splice(insertIndex, 0, subCodeRedeemLimitField);
				}
			} else {
				// Remove subCodeRedeemLimit field when sub-codes are disabled
				if (subCodeRedeemLimitFieldIndex >= 0) {
					fields.value.splice(subCodeRedeemLimitFieldIndex, 1);
				}
			}

			// IMPORTANT: Do NOT clear sub-codes when hasSubCodes is disabled
			// Sub-codes should NEVER be deleted as they serve as audit trail and history
			// Only clear sub-codes for new entities (when creating, not updating)
			if (!context.hasSubCodes && !context.id) {
				// Only clear for new entities (no ID means it's being created)
				context.subCodes = [];
			}
			// For existing entities, preserve sub-codes even when hasSubCodes is false
		}

		function transformSubCodes(formContext) {
			// Since sub-codes are managed through the dedicated dialog (not form fields),
			// we only need to handle preservation logic for existing entities

			if (!formContext.hasSubCodes && !formContext.id) {
				// Only clear sub-codes for new entities (no ID means it's being created)
				formContext.subCodes = [];
			}
			// For existing entities with hasSubCodes=false, preserve existing sub-codes
			// This ensures sub-codes are never accidentally deleted during updates

			// Transform perkRedemptionTimeControl for all perk configs
			if (formContext.perkConfigs && formContext.perkConfigs.length > 0) {
				formContext.perkConfigs.forEach(perkConfig => {
					if (perkConfig.perkRedemptionTimeControl) {
						// Convert time Date objects back to time strings for API
						if (perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited) {
							if (perkConfig.perkRedemptionTimeControl.lockedFromTimeDate) {
								perkConfig.perkRedemptionTimeControl.lockedFromTime = formatTimeFromDate(perkConfig.perkRedemptionTimeControl.lockedFromTimeDate);
							}
							if (perkConfig.perkRedemptionTimeControl.unlockedAtTimeDate) {
								perkConfig.perkRedemptionTimeControl.unlockedAtTime = formatTimeFromDate(perkConfig.perkRedemptionTimeControl.unlockedAtTimeDate);
							}
						} else {
							// Clear time values when time restrictions are disabled
							perkConfig.perkRedemptionTimeControl.lockedFromTime = null;
							perkConfig.perkRedemptionTimeControl.unlockedAtTime = null;
						}
						
						// Remove Date objects (used only for UI) before sending to API
						delete perkConfig.perkRedemptionTimeControl.lockedFromTimeDate;
						delete perkConfig.perkRedemptionTimeControl.unlockedAtTimeDate;
					}
				});
			}

			return formContext;
		}

	async function createEntity(formContext) {
		creating.value = true;

		try {
		// Validate freeDaysExpireAtDate is required when promoCodeType is Signup
		if (formContext.promoCodeType === 'Signup' && !formContext.freeDaysExpireAtDate) {
				toast.add({ 
					severity: 'warn', 
					summary: 'Validation Error', 
					detail: 'Free days expire at date is required for Signup promo codes', 
					life: 4000 
				});
				creating.value = false;
				return;
			}

			if (formContext.promoCodeType === 'Signup' && formContext.hasCustomSubscriptionPricing) {
				const validPrices = (formContext.subscriptionPriceConfigs ?? []).filter(c => c.price != null && c.price > 0);
				if (validPrices.length === 0) {
					toast.add({
						severity: 'warn',
						summary: 'Validation Error',
						detail: 'Please enable at least one subscription plan and set a price greater than 0.',
						life: 4000
					});
					creating.value = false;
					return;
				}
			}

			// Transform sub-codes before sending to API
			const transformedContext = transformSubCodes({ ...formContext });

			transformedContext.userId.id =
				await store.dispatch(storeActionCreateEntity, transformedContext);
			console.log(storeActionCreateEntity);

			toast.add({ severity: 'success', summary: 'Success', detail: entityName + ' created', life: 3000 });
			createNewSidebarVisible.value = false;

			loading.value = true;
			await reloadEntities();
		} catch (err) {
			toast.add({ severity: 'error', summary: 'Error', detail: 'Could not create ' + entityName + ': ' + err, life: 3000 });
		}

		loading.value = false;
		creating.value = false;
	}

	async function updateEntity(formContext) {
		updating.value = true;

		try {
		// Validate freeDaysExpireAtDate is required when promoCodeType is Signup
		if (formContext.promoCodeType === 'Signup' && !formContext.freeDaysExpireAtDate) {
				toast.add({ 
					severity: 'warn', 
					summary: 'Validation Error', 
					detail: 'Free days expire at date is required for Signup promo codes', 
					life: 4000 
				});
				updating.value = false;
				return;
			}

			if (formContext.promoCodeType === 'Signup' && formContext.hasCustomSubscriptionPricing) {
				const validPrices = (formContext.subscriptionPriceConfigs ?? []).filter(c => c.price != null && c.price > 0);
				if (validPrices.length === 0) {
					toast.add({
						severity: 'warn',
						summary: 'Validation Error',
						detail: 'Please enable at least one subscription plan and set a price greater than 0.',
						life: 4000
					});
					updating.value = false;
					return;
				}
			}

			// Transform sub-codes before sending to API
			const transformedContext = transformSubCodes({ ...formContext });

			await store.dispatch(storeActionUpdateEntity, transformedContext);

			toast.add({ severity: 'success', summary: 'Success', detail: entityName + ' updated', life: 3000 });
			updateSidebarVisible.value = false;

			loading.value = true;
			await reloadEntities();
		} catch (err) {
			toast.add({ severity: 'error', summary: 'Error', detail: 'Could not update ' + entityName + ': ' + err, life: 3000 });
		}

		loading.value = false;
		updating.value = false;
	}

		async function reloadEntities() {
			try {
				// Prepare filters for API call
				const apiFilters = { ...filters.value };

				// Convert "All" to appropriate backend value or remove it
				if (apiFilters.type === "All") {
					delete apiFilters.type;
				}

				// Remove empty code filter and includeSubCodes if no code is provided
				if (!apiFilters.code || apiFilters.code.trim() === "") {
					delete apiFilters.code;
					delete apiFilters.includeSubCodes; // No point in including sub-codes if no search term
				}

				// Remove empty campaign filter if not provided
				if (!apiFilters.campaign || apiFilters.campaign.trim() === "") {
					delete apiFilters.campaign;
				}

				const response = await store.dispatch(storeActionGetEntities, apiFilters);

				// Handle paginated response with new structure
				if (response && response.results && Array.isArray(response.results)) {
					entities.value = response.results;
					pagination.value.totalRecords = response.totalCount || 0;
				} else if (response && response.data && Array.isArray(response.data)) {
					// Fallback for old paginated response format
					entities.value = response.data;
					pagination.value.totalRecords = response.totalRecords || response.data.length;
				} else if (Array.isArray(response)) {
					// Fallback for non-paginated response
					entities.value = response;
					pagination.value.totalRecords = response.length;
				} else {
					// Use getter as fallback
					const loadedEntities = store.getters[storeGetterGetEntities];
					entities.value = loadedEntities || [];
					pagination.value.totalRecords = entities.value.length;
				}
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not load ' + pageTitle + ': ' + err, life: 3000 });
				entities.value = [];
				pagination.value.totalRecords = 0;
			}
		}


		async function loadMemberCards() {
			try {
				await store.dispatch('memberCards/ensureMemberCardListItems');
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not load Member Cards: ' + err, life: 3000 });
			}
			const loadedMemberCards = store.getters['memberCards/memberCardListItems'];
			memberCards.value = loadedMemberCards;
		}

		async function loadPerks() {
			try {
				await store.dispatch('perks/ensurePerkListItems');
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not load Perks: ' + err, life: 3000 });
			}
			const loadedPerks = store.getters['perks/perkListItems'];
			perks.value = loadedPerks;
		}

		async function applyFilter() {
			loading.value = true;
			// Only reset pagination if it's not a pagination-triggered filter
			if (!filters.value._isPaginationChange) {
				pagination.value.first = 0;
				pagination.value.page = 0;
				filters.value.page = 0;
			}
			// Remove the pagination flag if it exists
			delete filters.value._isPaginationChange;

			await reloadEntities()
			loading.value = false
		}

		function onPageChange(event) {
			pagination.value.first = event.first;
			pagination.value.page = event.page;
			pagination.value.pageSize = event.rows;

			filters.value.page = event.page;
			filters.value.pageSize = event.rows;
			filters.value._isPaginationChange = true;

			applyFilter();
		}

		function onCodeSearch() {
			// Clear existing timeout
			if (searchTimeout.value) {
				clearTimeout(searchTimeout.value);
			}

			// Set new timeout for debounced search
			searchTimeout.value = setTimeout(() => {
				// Reset to first page when searching
				pagination.value.first = 0;
				pagination.value.page = 0;
				filters.value.page = 0;
				applyFilter();
			}, 500); // 500ms delay
		}

		function onCampaignSearch() {
			// Clear existing timeout
			if (searchTimeout.value) {
				clearTimeout(searchTimeout.value);
			}

			// Set new timeout for debounced search
			searchTimeout.value = setTimeout(() => {
				// Reset to first page when searching
				pagination.value.first = 0;
				pagination.value.page = 0;
				filters.value.page = 0;
				applyFilter();
			}, 500); // 500ms delay
		}

		function openGenerateSubCodesDialog(promoCode) {
			selectedPromoCodeForGeneration.value = promoCode;
			generateSubCodesForm.value = {
				count: 10,
				length: 8,
				prefix: ''
			};
			generateSubCodesVisible.value = true;
		}

		async function generateSubCodes() {
			if (!selectedPromoCodeForGeneration.value) return;

			generatingSubCodes.value = true;

			try {
				const response = await apiClient.post('/PromoCodes/' + selectedPromoCodeForGeneration.value.id + '/generate-subcodes', generateSubCodesForm.value);

				const generatedCodes = response.data;

				toast.add({
					severity: 'success',
					summary: 'Success',
					detail: `Generated ${generatedCodes.length} sub-codes successfully`,
					life: 3000
				});

				generateSubCodesVisible.value = false;

				// Reload the entities to show updated sub-code count
				loading.value = true;
				await reloadEntities();
				loading.value = false;

				// If sub-codes management is open, reload the details
				if (subCodesManagementVisible.value && selectedPromoCodeForManagement.value?.id === selectedPromoCodeForGeneration.value.id) {
					await loadSubCodeDetails();
				}

			} catch (err) {
				toast.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Could not generate sub-codes: ' + err.message,
					life: 3000
				});
			}

			generatingSubCodes.value = false;
		}

		async function openSubCodesManagement(promoCode) {
			selectedPromoCodeForManagement.value = promoCode;
			subCodesManagementVisible.value = true;
			await loadSubCodeDetails();
		}

		async function loadSubCodeDetails() {
			if (!selectedPromoCodeForManagement.value) return;

			loadingSubCodeDetails.value = true;

			try {
				const response = await apiClient.get('/PromoCodes/' + selectedPromoCodeForManagement.value.id + '/subcodes');

				subCodeDetails.value = response.data;

			} catch (err) {
				toast.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Could not load sub-code details: ' + err.message,
					life: 3000
				});
				subCodeDetails.value = [];
			}

			loadingSubCodeDetails.value = false;
		}

		async function copyToClipboard(text) {
			try {
				await navigator.clipboard.writeText(text);
				toast.add({
					severity: 'success',
					summary: 'Copied',
					detail: 'Sub-code copied to clipboard',
					life: 2000
				});
			} catch (err) {
				toast.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Could not copy to clipboard',
					life: 3000
				});
			}
		}

		function exportSubCodesToCSV() {
			if (!subCodeDetails.value || subCodeDetails.value.length === 0) {
				toast.add({
					severity: 'warn',
					summary: 'No Data',
					detail: 'No sub-codes available to export',
					life: 3000
				});
				return;
			}

			try {
				// Prepare CSV headers
				const headers = ['Sub-code', 'Status', 'Number of redemptions', 'Redeem limit', 'Remaining redemptions', 'Last redemption timestamp'];

				// Prepare CSV rows
				const rows = subCodeDetails.value.map(subCode => [
					subCode.code,
					subCode.isRedeemed ? 'Redeemed' : 'Available',
					subCode.redemptionCount || 0,
					subCode.subCodeRedeemLimit > 0 ? subCode.subCodeRedeemLimit : 'Unlimited',
					subCode.subCodeRedeemLimit > 0 ? (subCode.remainingRedemptions ?? '') : 'Unlimited',
					subCode.lastRedemptionAt ? useFormatDate().formatDate(subCode.lastRedemptionAt) : ''
				]);

				// Combine headers and rows
				const csvContent = [headers, ...rows]
					.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
					.join('\n');

				// Create blob and download
				const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
				const link = document.createElement('a');

				// Generate filename with date and campaign name
				const now = new Date();
				const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
				const campaignName = selectedPromoCodeForManagement.value?.campaignName || 'PromoCode';
				const sanitizedCampaignName = campaignName.replace(/[^a-zA-Z0-9]/g, '_'); // Replace special chars with underscore
				const filename = `subcodes_${sanitizedCampaignName}_${dateStr}.csv`;

				if (link.download !== undefined) {
					const url = URL.createObjectURL(blob);
					link.setAttribute('href', url);
					link.setAttribute('download', filename);
					link.style.visibility = 'hidden';
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);
				}

				toast.add({
					severity: 'success',
					summary: 'Export Successful',
					detail: `Sub-codes exported to ${filename}`,
					life: 3000
				});

			} catch (err) {
				toast.add({
					severity: 'error',
					summary: 'Export Failed',
					detail: 'Could not export sub-codes: ' + err.message,
					life: 3000
				});
			}
		}

		async function loadRequiredData() {
			loading.value = true;

			
			await Promise.all([loadMemberCards(), loadPerks(), reloadEntities()])



			fields.value = [
				{
					id: 'promoCodeType',
					type: 'dropdown',
					title: 'Promo Code type',
					config: {
						options: [
							{ id: 'General', title: 'General' },
							{ id: 'Signup', title: 'Signup' },
							{ id: 'OnlySubscribers', title: 'OnlySubscribers' }
						],
						optionLabel: 'title',
						optionValue: 'id',
						placeholder: 'Select Promo Code',
					},
				},
				
				{
					id: 'campaignName',
					type: 'text-input',
					title: 'Campaign name',
				},
				{
					id: 'redeemLimit',
					type: 'number-input',
					title: 'Redeem limit',
				},
				{
					id: 'code',
					type: 'text-input',
					title: 'Code',
				},
				{
					id: 'hasSubCodes',
					type: 'switch',
					title: 'Enable Sub-codes',
					config: {
						tooltip: 'When enabled, this promo code will use multiple sub-codes instead of a single main code. The main code field will be hidden as it\'s not used with sub-codes.'
					}
				},
				{
					id: 'validFromUtc',
					type: 'calendar',
					title: 'Valid from',
				},
				{
					id: 'validToUtc',
					type: 'calendar',
					title: 'Valid to',
				},
				{
					id: 'hasCustomSubscriptionPricing',
					type: 'switch',
					title: 'Custom subscription pricing',
					condition: (ctx) => ctx.promoCodeType === 'Signup',
					config: {
						tooltip: 'When enabled, this promo code grants a custom price for one or more subscription plans when a new user signs up.'
					},
				},
				{
					id: 'subscriptionPriceConfigs',
					type: 'SubscriptionPriceConfigs',
					title: 'Subscription prices (DKK)',
					condition: (ctx) => ctx.promoCodeType === 'Signup' && ctx.hasCustomSubscriptionPricing,
				},
				{
					id: 'perkActiveDays',
					type: 'number-input',
					title: 'Perk active days',
				},
			
				{
					id: 'perkConfigs',
					type: 'PerkConfigs',
					title: 'Perk Configs',
				},
				{
					id: 'memberCardConfigs',
					type: 'MemberCardConfigs',
					title: 'Member Card Configs',
				},
				{
					id: 'hasOverrideExpiration',
					type: 'switch',
					title: 'Override member card expiration',
					config: {
						tooltip: 'Enable to set a custom expiration for member cards assigned via this promo code instead of using the member card\'s default.'
					},
				},
				{
					id: 'overrideExpirationUnit',
					type: 'dropdown',
					title: 'Override expiration unit',
					condition: (ctx) => ctx.hasOverrideExpiration,
					config: {
						options: [
							{ label: "Months", value: "Months" },
							{ label: "Days", value: "Days" },
							{ label: "Weeks", value: "Weeks" },
						],
						optionLabel: 'label',
						optionValue: 'value',
						placeholder: 'Select unit',
					},
				},
			{
				id: 'overrideExpirationValue',
				type: 'number-input',
				title: 'Override expiration value',
				condition: (ctx) => ctx.hasOverrideExpiration,
				help: 'Custom expiration for member cards assigned via this promo code. 0 = no expiration',
			},
		];

			loading.value = false;

			// Set initial form state
			handleFormChange(newEntity.value);
		}

		// Watch for page changes to clear selection
		watch(() => pagination.value.page, () => {
			selectedPromoCodes.value = [];
			selectAll.value = false;
		});

		// Watch for entities changes to update selectAll state
		watch(entities, () => {
			if (entities.value.length > 0 && selectedPromoCodes.value.length === entities.value.length) {
				selectAll.value = true;
			} else {
				selectAll.value = false;
			}
		});

		onMounted(async () => {
			await loadRequiredData();
		});

		return {
			entityName,
			pageTitle,
			pageDescription,
			loading,
			creating,
			updating,

			formattedPerks,
			getCreatedBy,

			fields,
			newEntity,
			selectedEntity,
			createEntity,
			updateEntity,
			confirmDeleteEntity,
			handleFormChange,
			applyFilter,

			memberCards,
			perks,

			entities,

			createNewSidebarVisible,
			updateSidebarVisible,

			typeFilterItems,
			filters,

			onRowClicked,

			formatDate: useFormatDate().formatDate,
			formatDateTimeFullTimezone: useFormatDate().formatDateTimeFullTimezone,

			pagination,
			searchTimeout,

			onPageChange,
			onCodeSearch,
			onCampaignSearch,

			generateSubCodesVisible,
			generatingSubCodes,
			generateSubCodesForm,
			selectedPromoCodeForGeneration,
			openGenerateSubCodesDialog,
			generateSubCodes,

			selectedPromoCodeForManagement,
			subCodesManagementVisible,
			subCodeDetails,
			loadingSubCodeDetails,
			subCodeSearchFilter,
			openSubCodesManagement,
			loadSubCodeDetails,
			copyToClipboard,

			subCodeExamples,
			filteredSubCodeDetails,

			exportSubCodesToCSV,

			// Bulk delete
			selectedPromoCodes,
			selectAll,
			toggleSelectAll,
			toggleSelection,
			isSelected,
			confirmBulkDelete,
		};
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
	min-height: 100vh;
	background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
	background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
	background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
	padding: 0.75rem;
	border-radius: 0.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Create button styling */
.create-button {
	background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
	border: none !important;
	padding: 0.75rem 1.5rem !important;
	border-radius: 0.75rem !important;
	font-weight: 600 !important;
	color: white !important;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
	transition: all 0.3s ease !important;
}

.create-button:hover {
	box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
	transform: translateY(-1px) !important;
}

/* Generate more button styling */
.generate-more-button {
	background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%) !important;
	border: none !important;
	padding: 0.5rem 1rem !important;
	border-radius: 0.5rem !important;
	font-weight: 500 !important;
	color: white !important;
	transition: all 0.2s ease !important;
}

.generate-more-button:hover {
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3) !important;
}

/* Export button styling */
.export-button {
	background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
	border: none !important;
	padding: 0.5rem 1rem !important;
	border-radius: 0.5rem !important;
	font-weight: 500 !important;
	color: white !important;
	transition: all 0.2s ease !important;
}

.export-button:hover {
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3) !important;
}

.export-button:disabled {
	background: #9ca3af !important;
	cursor: not-allowed !important;
	transform: none !important;
	box-shadow: none !important;
}

/* Search input styling */
.search-input {
	border-radius: 0.5rem 0 0 0.5rem !important;
	border: 1px solid #d1d5db !important;
	padding: 0.75rem !important;
	border-right: none !important;
	width: 100% !important;
	flex: 1 !important;
}

/* Ensure p-inputgroup fills width - scoped to filters only */
.filters-grid :deep(.p-inputgroup) {
	width: 100%;
	display: flex;
}

/* Ensure all filter inputs fill their container width - scoped to filters only */
.filters-grid :deep(.p-inputgroup .p-inputtext) {
	width: 100% !important;
	flex: 1 1 auto !important;
	min-width: 0 !important;
}

.filters-grid :deep(.p-dropdown) {
	width: 100% !important;
	/* display: block !important; */
}

.filters-grid :deep(.p-dropdown .p-dropdown-label) {
	width: 100%;
}

/* Ensure filter labels fill width */
.filter-column label.font-semibold {
	width: 100%;
	display: block;
}

/* Sub-codes search input styling (standalone) */
.subcodes-search-input {
	border-radius: 0.5rem !important;
	border: 1px solid #d1d5db !important;
	padding: 0.75rem !important;
	padding-left: 2.5rem !important;
}

.search-button {
	background: #3b82f6 !important;
	border: none !important;
	border-radius: 0 0.5rem 0.5rem 0 !important;
	color: white !important;
	border: 1px solid #3b82f6 !important;
}

/* Table header section */
.table-header-section {
	background: linear-gradient(90deg, #f9fafb 0%, #f1f5f9 100%);
	padding: 1.25rem 1.5rem;
	border-bottom: 1px solid #e5e7eb;
}

/* Ensure rounded corners are respected */
.border-round-t-2xl {
	border-top-left-radius: 1rem !important;
	border-top-right-radius: 1rem !important;
}

/* Status badge base styling */
.status-badge {
	padding: 0.5rem 1rem;
	border-radius: 0.75rem;
	font-weight: 500;
	text-align: center;
	min-width: max-content;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
	color: white;
	transition: all 0.2s ease;
	font-size: 0.875rem;
}

.status-badge:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Specific badge colors */
.type-badge {
	background: linear-gradient(135deg, #2dd4bf 0%, #06b6d4 100%);
}

.subcodes-badge {
	background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
}

.status-redeemed {
	background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.status-available {
	background: linear-gradient(135deg, #4ade80 0%, #10b981 100%);
}

/* Action buttons */
.action-button {
	padding: 0.5rem !important;
	width: 2.5rem !important;
	height: 2.5rem !important;
	border-radius: 0.5rem !important;
	border: none !important;
	transition: all 0.2s ease !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
}

.edit-button {
	background: #3b82f6 !important;
	color: white !important;
}

.edit-button:hover {
	background: #2563eb !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3) !important;
}

.delete-button {
	background: #ef4444 !important;
	color: white !important;
}

.delete-button:hover {
	background: #dc2626 !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3) !important;
}

.copy-button {
	background: transparent !important;
	color: #6b7280 !important;
	padding: 0.25rem !important;
	width: 1.5rem !important;
	height: 1.5rem !important;
}

.copy-button:hover {
	background: #f3f4f6 !important;
	color: #374151 !important;
}

.manage-button {
	background: #8b5cf6 !important;
	color: white !important;
	padding: 0.375rem 0.75rem !important;
	border-radius: 0.375rem !important;
	font-size: 0.75rem !important;
	font-weight: 500 !important;
}

.manage-button:hover {
	background: #7c3aed !important;
	transform: translateY(-1px) !important;
}

/* Modern sidebar styling */
:deep(.modern-sidebar .p-sidebar-content) {
	background: #f8fafc;
	padding: 0;
}

.promo-form-container {
	padding: 1.5rem;
	background: transparent;
	margin: 0;
	border-radius: 0;
	box-shadow: none;
}

/* Custom styling for modern datatable */
:deep(.modern-datatable) {
	border: none;
	border-radius: 0;
}

:deep(.modern-datatable .p-datatable-header) {
	background: transparent;
	border: none;
	padding: 0;
}

:deep(.modern-datatable .p-datatable-thead > tr > th) {
	background: transparent;
	border: none;
	padding: 1rem 1.5rem;
	font-weight: 600;
	color: #374151;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

:deep(.modern-datatable .p-datatable-tbody > tr) {
	background: white;
	border: none;
	transition: all 0.2s ease;
	cursor: pointer;
}

:deep(.modern-datatable .p-datatable-tbody > tr:hover) {
	background: #f8fafc;
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.modern-datatable .p-datatable-tbody > tr > td) {
	border: none;
	/* padding: 1.25rem 1.5rem; */
	border-bottom: 1px solid #f1f5f9;
}

:deep(.modern-datatable .p-datatable-tbody > tr:last-child > td) {
	border-bottom: none;
}

/* Striped rows with subtle styling */
:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even)) {
	background: #fdfdfe;
}

:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even):hover) {
	background: #f1f5f9;
}

/* Specific styling for subcodes datatable to ensure pagination visibility */
:deep(.subcodes-datatable) {
	height: 100%;
	display: flex;
	flex-direction: column;
}

:deep(.subcodes-datatable .p-datatable-wrapper) {
	flex: 1;
	overflow: auto;
}

:deep(.subcodes-datatable .p-paginator) {
	flex-shrink: 0;
	background: white;
	border-top: 1px solid #e5e7eb;
	padding: 1rem 1.5rem;
	margin: 0;
}

:deep(.subcodes-datatable .p-datatable-scrollable-body) {
	max-height: calc(100% - 60px);
	/* Reserve space for pagination */
}

/* Modern dialog styling */
:deep(.modern-dialog .p-dialog-header) {
	background: white;
	border-bottom: 1px solid #e5e7eb;
	border-radius: 0.75rem 0.75rem 0 0;
	padding: 1.5rem;
}

:deep(.modern-dialog .p-dialog-content) {
	background: white;
	border-radius: 0 0 0.75rem 0.75rem;
	padding: 0;
}

:deep(.modern-dialog .p-dialog) {
	border-radius: 0.75rem;
	box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
	border: none;
}

:deep(.modern-dialog .p-dialog-footer) {
	background: white;
	border-top: 1px solid #e5e7eb;
	border-radius: 0 0 0.75rem 0.75rem;
	padding: 1.5rem;
}

/* Loading and empty state styling */
.text-center i {
	display: block;
	margin: 0 auto;
}

/* Filters grid layout - ensures all 4 columns are equal width */
.filters-grid {
	display: grid;
	grid-template-columns: repeat(1, 1fr);
	gap: 1.5rem;
	width: 100%;
}

@media (min-width: 768px) {
	.filters-grid {
		grid-template-columns: repeat(4, 1fr);
	}
}

.filter-column {
	display: flex;
	flex-direction: column;
	width: 100%;
	min-width: 0; /* Prevent overflow */
}

.filter-column > * {
	width: 100%;
	max-width: 100%;
}

/* Grid system improvements (for other parts of the page) */
.grid {
	display: grid;
	grid-template-columns: repeat(12, 1fr);
	gap: 1rem;
	width: 100%;
}

.col-12 {
	grid-column: span 12;
	width: 100%;
	min-width: 0; /* Prevent grid blowout */
}

@media (min-width: 768px) {
	.md\:col-3 {
		grid-column: span 3;
		width: 100%;
		min-width: 0; /* Prevent grid blowout */
	}

	.md\:col-4 {
		grid-column: span 4;
		width: 100%;
		min-width: 0; /* Prevent grid blowout */
	}
}

/* Ensure filter columns and their contents fill width */
.grid > .col-12,
.grid > [class*="md:col-"] {
	width: 100%;
}

.grid > .col-12 > *,
.grid > [class*="md:col-"] > * {
	max-width: 100%;
}

/* Bulk delete button styling */
.bulk-delete-button {
	background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
	border: none !important;
	padding: 0.75rem 1.5rem !important;
	border-radius: 0.75rem !important;
	font-weight: 600 !important;
	color: white !important;
	box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.3) !important;
	transition: all 0.3s ease !important;
}

.bulk-delete-button:hover {
	box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.4) !important;
	transform: translateY(-1px) !important;
	background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
}

/* Checkbox styling */
:deep(.p-checkbox) {
	width: 1.25rem;
	height: 1.25rem;
}

:deep(.p-checkbox .p-checkbox-box) {
	border-radius: 0.375rem;
	border: 2px solid #d1d5db;
	transition: all 0.2s ease;
}

:deep(.p-checkbox .p-checkbox-box:hover) {
	border-color: #3b82f6;
}

:deep(.p-checkbox .p-checkbox-box.p-highlight) {
	background: #3b82f6;
	border-color: #3b82f6;
}

/* Checkbox header wrapper - align header checkbox with row checkboxes */
.checkbox-header-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
}

/* Checkbox cell wrapper - make entire cell clickable */
.checkbox-cell-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	padding: 2.0rem;
	cursor: pointer;
	transition: background-color 0.2s ease;
}

/* Only disable pointer events for checkboxes inside the wrapper (row checkboxes) */
.checkbox-cell-wrapper :deep(.p-checkbox) {
	pointer-events: none; /* Let the wrapper handle clicks */
}

.checkbox-cell-wrapper:hover :deep(.p-checkbox .p-checkbox-box) {
	border-color: #3b82f6;
}

/* Responsive improvements */
@media (max-width: 768px) {

	:deep(.modern-datatable .p-datatable-thead > tr > th),
	:deep(.modern-datatable .p-datatable-tbody > tr > td) {
		padding: 0.75rem 1rem;
	}

	.max-w-7xl {
		max-width: 100%;
	}

	.p-6 {
		padding: 1rem;
	}

	.status-badge {
		padding: 0.375rem 0.75rem;
		font-size: 0.875rem;
	}

	.grid {
		grid-template-columns: 1fr;
	}

	.col-12 {
		grid-column: span 1;
	}
}

/* Smooth scrolling for responsive table */
:deep(.p-datatable-wrapper) {
	border-radius: 0;
}

/* Ensure proper shadow styling */
.shadow-1 {
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Form styling improvements */
:deep(.promo-form .p-inputtext),
:deep(.promo-form .p-dropdown),
:deep(.promo-form .p-calendar),
:deep(.promo-form .p-inputnumber) {
	border-radius: 0.5rem;
	border: 1px solid #d1d5db;
}

:deep(.promo-form .p-inputtext:focus),
:deep(.promo-form .p-dropdown:focus),
:deep(.promo-form .p-calendar:focus),
:deep(.promo-form .p-inputnumber:focus) {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button improvements */
:deep(.promo-form .p-button) {
	border-radius: 0.5rem;
	font-weight: 500;
	transition: all 0.2s ease;
}

:deep(.promo-form .p-button:hover) {
	transform: translateY(-1px);
}
</style>